import React, { Fragment, useState, useEffect, useContext, useRef } from "react";
import Config from "../config.json";
import { decode } from "html-entities";
import parse from 'html-react-parser';
import ReactPaginate from "react-paginate";
import { useURLTokenDecode } from "../hooks/useURLTokenDecode";
import BlobCountContext from "../context/BlobCountContext";

import {
	Box,
	CircularProgress,
} from "@mui/material";

import SearchContext from "../context/SearchContext";

const FILE_LIST_LOGS = false;

function FileList({folder, 
		title
	}) {

	const { URLTokenDecode } = useURLTokenDecode();

	const { getDocuments, 
		documents, 
		totalCountOfMatches, 
		fetchError, 
		isSearching, 
		sortType, 
		sortOrder, 
		setSortType, 
		setSortOrder,
		isFirstSearchComplete,
		searchAfterNavigate,
		searchAfterSort } = useContext(SearchContext);

	const resultsPerPage = Config.search.resultsPerPage;
	const folderConfig = Config.storage.folders.find(
		(f) => f.name === folder
	);

	const [sortTypeLabel, setSortTypeLabel] = useState(folderConfig.defaultSortLabel);
	const [isSortByOpen, setIsSortByOpen] = useState(false);
	const [pageCount, setPageCount] = useState(0);
	const [pageMarker, setPageMarker] = useState(Config.search.pageRangeDisplayed);
	const blobCounts = useContext(BlobCountContext);

	var [documentsOffset, setDocumentsOffset] = useState(0);
	const isFirstRender = useRef(true);

	//#region useEffects

	useEffect(() => {
		if (FILE_LIST_LOGS) console.log("FileList => useEffect() => totalCountOfMatches", totalCountOfMatches);

		if (totalCountOfMatches === 0 && !fetchError && blobCounts[folder] !== undefined) {
			setPageCount(Math.ceil(blobCounts[folder] / resultsPerPage));
			return;
		}
		setPageCount(Math.ceil(totalCountOfMatches / resultsPerPage));
	}, [totalCountOfMatches, isSearching, resultsPerPage, fetchError, blobCounts, folder]);

	useEffect(() => {
		if (isFirstRender.current) {
			if (FILE_LIST_LOGS) console.log("FileList => useEffect() => sortOrder/sortType SKIPPED (first render)");
			return;
		}
		if (FILE_LIST_LOGS) console.log("FileList => useEffect() => sortOrder/sortType", sortOrder, sortType);
		resetPagination();
		searchAfterSort(folder);
	  // eslint-disable-next-line react-hooks/exhaustive-deps
	  }, [sortOrder, sortType]);


	useEffect(() => {
		if (FILE_LIST_LOGS) console.log("FileList => useEffect() => folder", folder);
		isFirstRender.current = false;
		resetPagination();
		setSortTypeLabel(folderConfig.defaultSortLabel);
		searchAfterNavigate(folder);
		window.scrollTo({
		  top: 0,
		  behavior: 'smooth'
		});
	  // eslint-disable-next-line react-hooks/exhaustive-deps
	  }, [folder]);
	
	  function resetPagination() {
		setPageCount(0);
		setDocumentsOffset(0);
		setPageMarker(Config.search.pageRangeDisplayed);
	  }

	  useEffect(() => {
		if (totalCountOfMatches === 0) {
			resetPagination();
		}
	  }, [totalCountOfMatches]);

	  //#endregion

	//#region eventHandlers
	  const changePage = ({ selected }) => {
		  if (FILE_LIST_LOGS)console.log("FileList => changePage => selected:", selected);
		  window.scrollTo(0, 0);
		  let selectedPageNum = selected;
		  setDocumentsOffset(selectedPageNum * resultsPerPage);
  
		  // load additional page ahead of time
		  if (selectedPageNum === pageMarker - 1) {
			  let newPageMarker = pageMarker + 1;
			  setPageMarker(newPageMarker);
			  getDocuments(folder, resultsPerPage, sortType, sortOrder, documents.length);
		  } else if (selectedPageNum === pageMarker) {
			  // load additional 2 pages ahead of time
			  let newPageMarker = pageMarker + 2;
			  setPageMarker(newPageMarker);
			  getDocuments(folder, resultsPerPage * 2, sortType, sortOrder, documents.length);
		  } else if (selectedPageNum > pageMarker) {
			  // backfill results
			  let numOfPagesToBackfill = selectedPageNum - pageMarker;
			  let backFillNum = numOfPagesToBackfill * resultsPerPage;
  
			  // load additional 2 pages ahead of time
			  let newPageMarker = selectedPageNum + 2;
			  setPageMarker(newPageMarker);
			  let additionalNum = Config.search.resultsPerPage * 2;
  
			  getDocuments(folder, backFillNum + additionalNum, sortType, sortOrder, documents.length);
		  }
	  };

	  function onClickSortOrderSelected(e) {
		  let value = e.target.getAttribute("value")
		  if (FILE_LIST_LOGS) console.log("FileList => onClickSortOrderSelected => value:", value);
		  setSortOrder(value);
		  setIsSortByOpen(false);
	  }
  
	  function onClickSortTypeSelected(e, label) {
		  let value = e.target.getAttribute("value")
		  if (FILE_LIST_LOGS) console.log("FileList => onClickSortTypeSelected => value:", value);
		  setSortType(value);
		  setSortTypeLabel(label);
		  setIsSortByOpen(false);
	  }
	  //#endregion
	  
	//#region renderSortByDropdown
	  function renderSortByDropDown() {
		  if (documents != null && documents.length > 0) {
			  return (
				  <div id="sort-by-container" className="ml-3 mb-3">
					  <button
						  id="sort-by-button"
						  type="button"
						  className="btn btn-outline-primary"
						  onClick={handleSortByButtonClick}
					  >
						  <i
							  className={
								  sortOrder === "asc"
									  ? "fa fas fa-sort-amount-up"
									  : "fa fas fa-sort-amount-down"
							  }
						  ></i>
						  Sort by: {sortTypeLabel}
					  </button>
					  {isSortByOpen && (
						  <div id="sort-by-flyout">
							  <ul aria-labelledby="sort-by-button">
								  <div
									  id="sort-order-container"
									  className="btn-group"
									  role="group"
									  aria-label="Sort order options"
								  >
									  <button
										  type="button"
										  onClick={onClickSortOrderSelected}
										  className={sortOrder === "asc" ? "selected" : ""}
										  value="asc"
									  >
										  Asc
									  </button>
									  <button
										  type="button"
										  onClick={onClickSortOrderSelected}
										  className={sortOrder === "desc" ? "selected" : ""}
										  value="desc"
									  >
										  Desc
									  </button>
								  </div>
								  {folderConfig.sortOptions?.map((m, i) => (
									  <li
										  key={i}
										  onClick={(e) => {
											  onClickSortTypeSelected(e, m.label);
										  }}
										  value={m.value + "_" + m.datatype}
										  className={sortType.split("_")[0] === m.value ? "selected" : ""}
									  >
										  <span>
											  {sortType.split("_")[0] === m.value && (
												  <i className="fa fas fa-check" aria-hidden="true"></i>
											  )}
										  </span>
										  {m.label}
									  </li>
								  ))}
							  </ul>
						  </div>
					  )}
				  </div>
			  );
		  }
	  }
	  function handleSortByButtonClick() {
		  setIsSortByOpen(!isSortByOpen);
	  }
  
	  //#endregion
  
	//#region renderDocuments
	function renderDocuments() {
		if (isSearching) {
			return (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						p: 3, // padding: 3 * 8px = 24px
					}}
				>
					<CircularProgress />
					<span
						id="document-spinner-text"
						className="mt-2"
						aria-hidden="true"
					>
						Loading results...
					</span>
				</Box>
			);
		} else if (documents != null && documents.length > 0) {
			// render documents if they exist
			return documents.slice(documentsOffset, documentsOffset + resultsPerPage)
			.map((doc, i) => (
				<div key={i} className="card mb-4">
					<div className="card-header p-0">
						<h2 className="card-title h4 my-0">
							<a
								className="d-block p-3"
								target="_blank"
								rel="noreferrer"
								href={URLTokenDecode(doc.metadata_storage_path)}
								aria-label={
									doc.metadata_storage_path.toLowerCase().includes(".pdf")
										? "Link to open .pdf file in new tab"
										: "Link to download file"
								}
							>
								{doc.metadata_storage_name}{" "}
								<i
									className={
										doc.metadata_storage_name.toLowerCase().includes(".pdf")
											? "fa fa-external-link-alt"
											: "fa fa-download"
									}
									aria-hidden="true"
								></i>
							</a>
						</h2>
					</div>
					<div className="card-body">
						{renderMetadata(doc)}
						{renderDocumentText(doc)}
					</div>
				</div>
			));
		} else if (fetchError) {
			return (
				<div className="col-12 text-danger">
					<hr />
					<p>
						Unable to retrieve records. Contact recordshelp@bellevuewa.gov for
						inquiries.
					</p>
				</div>
			);
		} else if (documents.length === 0 && !isSearching && isFirstSearchComplete) {
			return (
				<div className="col-12 text-danger">
					<hr />
					<p>No records match the selected filters.</p>
				</div>
			);
		} else {
			return (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						p: 3, // padding: 3 * 8px = 24px
					}}
				>
					<CircularProgress />
					<span
						id="document-spinner-text"
						className="mt-2"
						aria-hidden="true"
					>
						Loading results...
					</span>
				</Box>
			);
		}
	}
	
	function renderMetadata(record) {
		return (		
			<dl className="card-text row mb-2">
				<dt className="col-md-4 col-sm-12 ">Category:</dt>
				<dd className="col-md-8 col-sm-12">
					{record.prefix.replace(/([A-Z])/g, " $1").trim()}
				</dd>
				{folderConfig.metadata?.map(
					(m, i) =>
						record[m.id] !== undefined &&
						record[m.id] !== null && (
							<Fragment key={i}>
								<dt className="col-md-4 col-sm-12 ">
									{m.name}:
								</dt>
								<dd className="col-md-8 col-sm-12">
									{Config.storage.metadataDateTimeFields.includes(m.id)
										? formatDate(record[m.id])
										: decode(record[m.id])}
								</dd>
							</Fragment>
						)
				)}
			</dl>
		);
	}

	function formatDate(dateString) {
		if (!dateString) return "";
		const d = new Date(dateString);
		if (Number.isNaN(d.getTime())) return "";
		// Pad month and day with leading zeros
		const month = String(d.getUTCMonth() + 1).padStart(2, '0');
		const day = String(d.getUTCDate()).padStart(2, '0');
		const year = d.getUTCFullYear();
		return `${month}-${day}-${year}`;
	  }

    function renderDocumentText(doc) {
        return (
			<Fragment>
				{doc["@search.highlights"]?.content && <hr />}
                {doc["@search.highlights"]?.content?.map((c, i) => (
                    <p key={i} className="card-text">{parse(c)} </p>
                ))}
            </Fragment>
        )
    }

	//#endregion
	
	//#region renderPagination
	function renderPagination() {
		if (documents.length > 0 && pageCount > 0) {
			return (
				<ReactPaginate
					key={`${folder}-${sortType}-${sortOrder}`}
					pageLinkClassName={"page-link"}
					pageClassName={"page-item"}
					previousLabel={"Previous"}
					nextLabel={"Next"}
					pageCount={pageCount}
					onPageChange={changePage}
					containerClassName={"pagination justify-content-center"}
					previousLinkClassName={"page-link"}
					previousClassName={"page-item"}
					nextClassName={"page-item"}
					nextLinkClassName={"page-link"}
					disabledClassName={"disabled"}
					activeClassName={"active"}
					breakClassName={"page-item"}
					breakLinkClassName={"page-link"}
					pageRangeDisplayed={Config.storage.pageRangeDisplayed - 1}
					marginPagesDisplayed={0}
				/>
			);
		}
	}
    //#endregion

	//#region render
	return (
		<Fragment>
			{folderConfig.hidden === true && (
				<meta name="robots" content="noindex" />
			)}
			<main className="col-sm-12 col-md-8 col-lg-9 mt-3 mb-5">
				<div className="d-flex justify-content-between">
					<div className="">
						{blobCounts[folder] === undefined && (
							<p className="document-total document-total-loading">Loading document count</p>
						)}
						{blobCounts[folder] !== undefined && (
						<p className="document-total">{totalCountOfMatches > 0 && totalCountOfMatches * 1.01 < blobCounts[folder] && (
							<span>{totalCountOfMatches} of </span>
						)}{blobCounts[folder]} {
							title}</p>
						)}
					</div>
					{renderSortByDropDown()}
				</div>
				<div id="documents">
					{renderDocuments()}
				</div>
				<div className="row">
					{" "}
					<div className="col align-self-center">{renderPagination()}</div>
				</div>
			</main>
		</Fragment>
	);
	//#endregion


}

export default FileList;
